/**
 * Shared India (Asia/Kolkata) date/time formatting for the whole Thaai app.
 * All datetimes that include a clock use 12-hour format with AM/PM.
 */

const IST = "Asia/Kolkata";

function toValidDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  // Date-only (YYYY-MM-DD) → local calendar day (no UTC midnight shift)
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const d = new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
      12,
      0,
      0,
      0
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Normalize locale time like "3:24 pm" / "03:24 pm" → "03:24 PM" */
function normalizeAmPmTime(timePart: string): string {
  const cleaned = timePart
    .replace(/\u202f/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const m = cleaned.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)$/i);
  if (!m) return cleaned.replace(/\b(am|pm)\b/gi, (x) => x.toUpperCase());
  return `${m[1].padStart(2, "0")}:${m[2]} ${m[3].toUpperCase()}`;
}

/** Normalize customer role keys. */
export function normalizeCustomerRole(customerRole?: string | null): string {
  return String(customerRole || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
}

/**
 * Label for `toBeDelivered`:
 *   dealer     → Company Delivery Date
 *   interior / end-user → Fixing Date
 */
export function getToBeDeliveredFieldLabel(customerRole?: string | null): string {
  const role = normalizeCustomerRole(customerRole);
  if (role === "dealer") return "Company Delivery Date";
  if (role === "interior" || role === "end-user" || role === "enduser") {
    return "Fixing Date";
  }
  return "Fixing Date";
}

/** Parse API/date-only values into a local Date for calendar pickers (no UTC shift). */
export function parseLocalDate(value: string | Date | null | undefined): Date | undefined {
  const d = toValidDate(value);
  return d || undefined;
}

/** Local calendar YYYY-MM-DD from a Date (no UTC shift). */
export function toLocalDateString(value: Date | null | undefined): string | null {
  if (!value || Number.isNaN(value.getTime())) return null;
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * RFC3339 timestamp at noon IST for a calendar date.
 * Go's time.Time JSON binding rejects date-only strings like "2026-08-14".
 */
export function toApiDate(value: Date | string | null | undefined): string | null {
  if (value == null || value === "") return null;
  let ymd: string | null = null;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    ymd = m ? m[1] : toLocalDateString(parseLocalDate(value));
  } else {
    ymd = toLocalDateString(value);
  }
  if (!ymd) return null;
  return `${ymd}T12:00:00+05:30`;
}

/** DD-MM-YYYY in Asia/Kolkata (no time). */
export function formatIndiaDate(
  value: string | Date | null | undefined,
  empty = "N/A"
): string {
  const d = toValidDate(value);
  if (!d) return empty;
  return d
    .toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: IST,
    })
    .replace(/\//g, "-");
}

/**
 * DD-MM-YYYY hh:mm AM/PM in Asia/Kolkata (12-hour).
 */
export function formatIndiaDateTime(
  value: string | Date | null | undefined,
  empty = "N/A"
): string {
  const d = toValidDate(value);
  if (!d) return empty;

  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: IST,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value || "";

  const datePart = `${get("day")}-${get("month")}-${get("year")}`;
  const timePart = normalizeAmPmTime(`${get("hour")}:${get("minute")} ${get("dayPeriod")}`);
  return `${datePart} ${timePart}`;
}

export const formatIndiaTime = formatIndiaDateTime;
export const formatDateOnly = formatIndiaDate;

/**
 * Quotation date + user-selected 12h time string (e.g. "03:23 PM").
 * Time is shown exactly as selected/stored — not derived from createdAt.
 */
export function formatQuotationDateTime(
  quotationDate?: string | Date | null,
  quotationTime?: string | null,
  fallbackDate?: string | Date | null
): string {
  const dateSrc = quotationDate || fallbackDate;
  if (!dateSrc && !quotationTime) return "N/A";

  let datePart = "N/A";
  if (dateSrc) {
    datePart = formatIndiaDate(dateSrc);
  }

  const rawTime = quotationTime != null ? String(quotationTime).trim() : "";
  if (rawTime) {
    const time = normalizeAmPmTime(rawTime);
    return datePart !== "N/A" ? `${datePart} ${time}` : time;
  }
  return datePart;
}

export function shouldShowCustomerContactOnProductionCopy(
  customerRole?: string | null
): boolean {
  const role = normalizeCustomerRole(customerRole);
  return role === "interior" || role === "end-user" || role === "enduser";
}
